"use client";

import { c as _c } from "react/compiler-runtime";
import css from "../resources/resource/resource.module.scss";
import { PortableText } from "next-sanity";
import InternalLink from "@/components/common/internal-link";
import Image from "../common/image/image";
import imageUrl from "@/sanity/utilities/imageUrl";
import { ResourceHubPage } from "@/sanity/sanity.types";
import ArticleBreadCrumb from "./ArticleBreadCrumb";
import SuperTitle from "../common/supertitle/supertitle";
import GridRow from "@/components/common/grid-row/grid-row";
import GridColumn from "@/components/common/grid-column/grid-column";
import Link from "next/link";
import ArrowIcon, { ArrowIconDirection, ArrowIconStyle } from "@/components/common/arrow-icon/arrow-icon";
import Articles from "../articles/articles";
import { ArticleData, AuthorPageData } from "@/sanity/interfaces";
import { usePerformance } from "@/contexts/performance-context";
import { useEffect } from "react";
interface IResourceProps {
  data: AuthorPageData;
  latestArticles?: ArticleData[];
  currentPage: number;
  totalPages: number;
  landingPageData: ResourceHubPage;
  beforeParamTime: number;
  beforeQueryTime: number;
  afterQueryTime: number;
  authName?: string;
}
const PAGE_WINDOW = 5;
const AuthorName = props => {
  const $ = _c(60);
  const {
    data,
    latestArticles,
    currentPage,
    totalPages,
    landingPageData,
    beforeParamTime,
    beforeQueryTime,
    afterQueryTime,
    authName
  } = props;
  const person = data?.person;
  const basePath = `/learn/author/${authName}`;
  const {
    checkTimeElapsed
  } = usePerformance();
  let t0;
  if ($[0] !== basePath) {
    t0 = page => {
      if (page <= 1) {
        return basePath;
      }
      return `${basePath}?page=${page}`;
    };
    $[0] = basePath;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const getPageHref = t0;
  const startPage = Math.max(1, Math.min(currentPage, totalPages - PAGE_WINDOW + 1));
  const endPage = Math.min(totalPages, startPage + PAGE_WINDOW - 1);
  let t1;
  if ($[2] !== afterQueryTime || $[3] !== beforeParamTime || $[4] !== beforeQueryTime || $[5] !== checkTimeElapsed) {
    t1 = () => {
      checkTimeElapsed(beforeParamTime, "Author page before param started");
      checkTimeElapsed(beforeQueryTime, "Author page before query started");
      checkTimeElapsed(afterQueryTime, "Author page after query completed");
    };
    $[2] = afterQueryTime;
    $[3] = beforeParamTime;
    $[4] = beforeQueryTime;
    $[5] = checkTimeElapsed;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [];
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  useEffect(t1, t2);
  let t3;
  if ($[8] !== landingPageData || $[9] !== person) {
    t3 = person && landingPageData && <ArticleBreadCrumb data={person} landingPageData={landingPageData} />;
    $[8] = landingPageData;
    $[9] = person;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== t3) {
    t4 = <div className="flex flex-col gap-8 md:flex-row md:items-start">{t3}</div>;
    $[11] = t3;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <div className="flex items-start"><SuperTitle className="text-center text-[14px] font-bold uppercase leading-[24px] tracking-[1.12px] text-[#009490]">About Author</SuperTitle></div>;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const t6 = person?.name;
  let t7;
  if ($[14] !== t6) {
    t7 = <div className="flex w-full flex-col"><h1 className="font-lora text-[38px] font-semibold leading-normal tracking-[0.2px] text-[#00698F]">{t6}</h1></div>;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const t8 = person?.role;
  let t9;
  if ($[16] !== t8) {
    t9 = <p className="text-[16px] font-semibold leading-[26px] text-[#373A36] md:text-[24px]">Author | {t8} at AmeriSave</p>;
    $[16] = t8;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== t7 || $[19] !== t9) {
    t10 = <div className="w-full flex-col items-start justify-start md:hidden">{t5}{t7}{t9}</div>;
    $[18] = t7;
    $[19] = t9;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== person) {
    t11 = person?.photo?.asset && <Image src={imageUrl(person?.photo?.asset).url()} alt={person?.name ?? ""} className="block w-full rounded-lg object-cover" width={358} height={354} srcSet={`
                      ${imageUrl(person.photo.asset).width(560).url()} 560w,
                      ${imageUrl(person.photo.asset).width(345).url()} 345w
                    `} sizes="(width <= 640px) 560px, 345px" />;
    $[21] = person;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== t11) {
    t12 = <div className="mt-5 w-full">{t11}</div>;
    $[23] = t11;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] !== t10 || $[26] !== t12) {
    t13 = <div className="flex w-full flex-col items-center md:w-[30%] md:items-start">{t10}{t12}</div>;
    $[25] = t10;
    $[26] = t12;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  let t14;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <div className="flex items-start"><SuperTitle>About Author</SuperTitle></div>;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  const t15 = person?.name;
  let t16;
  if ($[29] !== t15) {
    t16 = <div className="flex w-full flex-col"><h1 className="self-stretch font-lora text-[38px] font-semibold leading-normal tracking-[0.2px] text-[#00698F]">{t15}</h1></div>;
    $[29] = t15;
    $[30] = t16;
  } else {
    t16 = $[30];
  }
  const t17 = person?.role;
  let t18;
  if ($[31] !== t17) {
    t18 = <p className="text-[24px] font-semibold leading-[26px] text-[#373A36]">Author | {t17} at AmeriSave</p>;
    $[31] = t17;
    $[32] = t18;
  } else {
    t18 = $[32];
  }
  let t19;
  if ($[33] !== t16 || $[34] !== t18) {
    t19 = <div className="hidden md:block">{t14}{t16}{t18}</div>;
    $[33] = t16;
    $[34] = t18;
    $[35] = t19;
  } else {
    t19 = $[35];
  }
  const t20 = person?.bio;
  let t21;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = {
      types: {
        undefined: _temp
      },
      marks: {
        unknownMark: _temp2,
        internalLink: _temp3
      }
    };
    $[36] = t21;
  } else {
    t21 = $[36];
  }
  let t22;
  if ($[37] !== t20) {
    t22 = <div className="prose max-w-none pt-4 text-[20px] font-normal leading-[36px] text-[#4A4A4A]"><PortableText value={t20} components={t21} /></div>;
    $[37] = t20;
    $[38] = t22;
  } else {
    t22 = $[38];
  }
  let t23;
  if ($[39] !== t19 || $[40] !== t22) {
    t23 = <div className="relative my-5 flex w-full flex-col text-left md:w-[70%] md:gap-1">{t19}{t22}</div>;
    $[39] = t19;
    $[40] = t22;
    $[41] = t23;
  } else {
    t23 = $[41];
  }
  let t24;
  if ($[42] !== t13 || $[43] !== t23) {
    t24 = <div className="mb-8 mt-6 flex w-full flex-col md:mt-6 md:flex-row md:items-start md:gap-8">{t13}{t23}</div>;
    $[42] = t13;
    $[43] = t23;
    $[44] = t24;
  } else {
    t24 = $[44];
  }
  let t25;
  if ($[45] !== t24 || $[46] !== t4) {
    t25 = <GridRow><GridColumn>{t4}{t24}</GridColumn></GridRow>;
    $[45] = t24;
    $[46] = t4;
    $[47] = t25;
  } else {
    t25 = $[47];
  }
  let t26;
  if ($[48] !== latestArticles) {
    t26 = latestArticles && <Articles data={latestArticles} />;
    $[48] = latestArticles;
    $[49] = t26;
  } else {
    t26 = $[49];
  }
  let t27;
  if ($[50] !== currentPage || $[51] !== endPage || $[52] !== getPageHref || $[53] !== startPage || $[54] !== totalPages) {
    t27 = totalPages > 1 && <nav className="mt-8 flex items-center justify-center gap-1 md:mt-[10px]" aria-label="Pagination">{currentPage > 1 ? <Link href={getPageHref(currentPage - 1)} rel="prev" className="flex items-center rounded-md px-4 py-2"><ArrowIcon direction={ArrowIconDirection.LEFT} className="ml-0 mr-2" style={ArrowIconStyle.SECONDARY} />Previous</Link> : <span className="mr-3 flex items-center rounded-md px-4 py-2 opacity-50"><ArrowIcon direction={ArrowIconDirection.LEFT} className="ml-0 mr-2" style={ArrowIconStyle.SECONDARY} />Previous</span>}{startPage > 1 && <span className="px-2 py-2 text-sm">...</span>}{Array.from({
        length: endPage - startPage + 1
      }, (_, index) => {
        const page_0 = startPage + index;
        const isActive = page_0 === currentPage;
        return isActive ? <span key={page_0} aria-current="page" className="rounded-md bg-[#373B37] px-3 py-2 text-sm text-white">{page_0}</span> : <Link key={page_0} href={getPageHref(page_0)} className="rounded-md px-5 py-2 text-sm hover:bg-gray-200">{page_0}</Link>;
      })}{endPage < totalPages && <span className="px-2 py-2 text-sm">...</span>}{currentPage < totalPages ? <Link href={getPageHref(currentPage + 1)} rel="next" className="flex items-center rounded-md px-4 py-2">Next<ArrowIcon direction={ArrowIconDirection.RIGHT} className="ml-0 mr-2" style={ArrowIconStyle.SECONDARY} /></Link> : <span className="ml-3 flex items-center rounded-md px-4 py-2 opacity-50">Next<ArrowIcon direction={ArrowIconDirection.RIGHT} className="ml-0 mr-2" style={ArrowIconStyle.SECONDARY} /></span>}</nav>;
    $[50] = currentPage;
    $[51] = endPage;
    $[52] = getPageHref;
    $[53] = startPage;
    $[54] = totalPages;
    $[55] = t27;
  } else {
    t27 = $[55];
  }
  let t28;
  if ($[56] !== t25 || $[57] !== t26 || $[58] !== t27) {
    t28 = <div className={`${css.resource} my-12`}>{t25}{t26}{t27}</div>;
    $[56] = t25;
    $[57] = t26;
    $[58] = t27;
    $[59] = t28;
  } else {
    t28 = $[59];
  }
  return t28;
};
export default AuthorName;
function _temp() {
  return null;
}
function _temp2() {
  return null;
}
function _temp3(t0) {
  const {
    children,
    value
  } = t0;
  return <InternalLink value={value}>{children}</InternalLink>;
}